import { Button, Flex, Text, VStack } from "@chakra-ui/react";
import { Step, Steps, useSteps } from "chakra-ui-steps";
import { StepOrderForm } from "./StepOrderForm";
import { FiChevronLeft } from "react-icons/fi";
import { ReviewsAndPayments } from "./ReviewsAndPayments";
import { SuccessPage } from "./SuccessPage";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { UserProfileContext } from "../../../context/UserContext";
import { PaymentModal } from "./PaymentModal";
import { initializePayment } from "../services/initializePayment";
import { getCheckoutQuote } from "../services/couponService";
import {
  errorNotifier,
  successNotifier,
} from "../../../components/NotificationHandler";
import { verifyPayment } from "../services/verifyPayment";
import {
  setStoredPendingPayment,
  clearStoredPendingPayment,
} from "../services/pendingPaymentContext";
import { getDashboardData } from "../../dashboard/services/getDashboardData";
import { setCommodityRates, setDashboardData } from "../../../store/actions/displayAction";
import FullPageLoader from "../../../components/fullPageLoader";
import { residentPaymentSettings, resolveServerRate, blockingReasonMessages } from "../../../utils/residentPaymentSettings";
import { commodityUnit, estateCommodityUnit } from "../../../utils/commodityUnits";

export const ProgressSteps = (props) => {
  const { activeAccount, userProfile } = useContext(UserProfileContext) || {};
  // `attempt` bumps when the resident retries after a failed/cancelled
  // payment, remounting the flow so no quote/coupon/billId/reference/method
  // survives. The estate id in the key does the same on estate switches.
  const [attempt, setAttempt] = useState(0);
  const { onDismissResume, ...rest } = props;
  const handleRetry = () => {
    clearStoredPendingPayment();
    if (typeof onDismissResume === "function") onDismissResume();
    setAttempt((a) => a + 1);
  };
  return (
    <PaymentSteps
      key={`${userProfile?._id || "resident"}:${activeAccount?.estateId || "none"}:${attempt}`}
      onRetry={handleRetry}
      {...rest}
    />
  );
};

const parsePaymentError = (e) => {
  const raw = e?.response?.data?.data;
  const fieldError = e?.response?.data?.errors?.[0]?.msg;
  if (/DISABLE_|kill.?switch|subaccount|secret|api.?key|integration|sk_live_|sk_test_/i.test(JSON.stringify(e?.response?.data || {}))) {
    return "Payment is temporarily unavailable. Please contact your estate administrator.";
  }

  if (fieldError) return fieldError;

  if (typeof raw === "string") {
    // Some backend errors embed a JSON blob after a prefix, e.g.
    // "Unable to initiate payment: {\"message\":...}" — extract it when
    // present.
    const jsonStart = raw.indexOf("{");
    if (jsonStart !== -1) {
      try {
        const { message, meta } = JSON.parse(raw.slice(jsonStart));
        if (message || meta?.nextStep) return message || meta?.nextStep;
      } catch (_) {
        // fall through to the plain-string case below
      }
    }
    // A plain, already resident-safe message (e.g. "Your estate's online
    // payment setup is not complete yet...") — surface it directly rather
    // than discarding it for a generic fallback.
    if (raw.trim()) return raw;
  }

  return "Payment initiation failed. Please try again.";
};

// Debounce delay for re-fetching the server-authoritative checkout quote —
// avoids a request per keystroke while entering an amount.
const QUOTE_DEBOUNCE_MS = 350;

const PaymentSteps = ({
  setDisplayPage,
  dispayPage,
  userProfile,
  initialAmount,
  resumedPayment,
  onRetry,
}) => {
  const url = window.location.href;
  const urlString = new URL(url);
  const amount = urlString.searchParams?.get("amount");

  const [amountPaid, setAmountPaid] = useState(
    () => initialAmount ?? amount ?? "",
  );
  const [unit, setUnit] = useState("");
  const [showSuccessPage, setShowSuccessPage] = useState(false);
  const [paymentData, setPaymentData] = useState({});
  const [paymentModal, setPaymentModal] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [appliedCoupons, setAppliedCoupons] = useState([]); // [{code, valueInNaira}]
  const [token, setToken] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState("CONFIRMED");
  // Rate self-hydration — a resident landing directly on /buy-water (fresh
  // login, browser refresh on this page, or after switching estates) may not
  // have commodityRates populated yet, since it is normally only set as a
  // side effect of visiting the Dashboard. Never default to a zero/hardcoded
  // rate and never require a Dashboard visit first (Checkpoint 2B2 Final
  // Closure) — fetch it here if it's missing.
  const [rateLoading, setRateLoading] = useState(false);
  const [rateUnavailable, setRateUnavailable] = useState(false);

  // Checkpoint 2C — server-authoritative checkout quote. This, not any
  // client-side arithmetic, is what the review/payment step displays and
  // what /order/initiate is asked to honor (via quoteFingerprint). Never
  // reserves or consumes a coupon — purely a preview, safe to refetch on
  // every amount/coupon change.
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState(null);
  const quoteRequestSeq = useRef(0);
  // Set right before syncing appliedCoupons from a QUOTE_CHANGED response —
  // that response IS the fresh authoritative quote for that exact coupon
  // set, so the debounced fetch effect below (which appliedCoupons is a
  // dependency of) should skip firing a redundant, momentarily-stale-again
  // request rather than discarding it and re-disabling Buy while it repeats
  // a call whose answer we already have.
  const skipNextQuoteFetchRef = useRef(false);

  const dispatch = useDispatch();

  const utilityType = useSelector(
    (state) => state?.switchTab?.currentDisplay?.utilityType,
  );

  const commodity = "water";

  const storedRate = useSelector(
    (state) => state?.switchTab?.commodityRates?.[commodity],
  );

  const { activeAccount, isLoading: profileLoading } =
    useContext(UserProfileContext) || {};

  // The estate payload from GET /users/estates is the authoritative source
  // for the rate. Only when it carries no rate information at all (legacy
  // payload) do we fall back to the dashboard-hydrated Redux rate. A missing
  // or 0 authoritative rate is "Rate unavailable" — never a fallback value.
  const serverRate = resolveServerRate(activeAccount, commodity);
  const RATE = serverRate.authoritative
    ? serverRate.rate
    : storedRate && storedRate > 0
      ? storedRate
      : 0;
  const rateBlocked = serverRate.authoritative ? !(RATE > 0) : rateUnavailable;
  const unitLabel = estateCommodityUnit(activeAccount?.commodityUnits, commodity);

  const estateKey = activeAccount?.estateId || "default";
  const estateId = activeAccount?.estateId || null;
  const settings = residentPaymentSettings(activeAccount);
  const [chosenPaymentMethod, setChosenPaymentMethod] = useState("");
  // Rails come only from the server's *Available flags: one rail -> that
  // rail; two -> the resident chooses; none -> fail closed.
  const paymentMethod = settings.rails.length === 1 ? settings.rails[0] : settings.rails.includes(chosenPaymentMethod) ? chosenPaymentMethod : "";
  const isBankTransfer = paymentMethod === "BANK_TRANSFER";
  const initiationRef = useRef(false);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; quoteRequestSeq.current++; }, []);

  // Primary source: dashboard API response stored in Redux when the dashboard loaded.
  // Fallback: context activeAccount.meterNumbers (populated from user profile +
  // generalMeter fix), then legacy houseId field.
  const dashboardMeterNumber = useSelector(
    (state) =>
      state?.switchTab?.dashboardData?.[estateKey]?.water?.creditCard
        ?.meterNumber,
  );

  const meterId =
    (dashboardMeterNumber && dashboardMeterNumber !== "N/A"
      ? dashboardMeterNumber
      : null) || activeAccount?.meterNumbers?.water;

  const { nextStep, prevStep, activeStep } = useSteps({
    initialStep: 0,
  });

  const navigate = useNavigate();

  // Fetch the live commodity rate directly if it isn't already in Redux —
  // makes /buy-water self-sufficient regardless of navigation history
  // instead of silently defaulting to a rate of 0.
  useEffect(() => {
    if (RATE > 0) {
      setRateUnavailable(false);
      return;
    }
    if (serverRate.authoritative) {
      // The server says there is no usable rate — do not go looking for one.
      setRateUnavailable(true);
      return;
    }
    const estId = activeAccount?.estateId || null;
    if (!estId) return; // estate context not resolved yet — effect re-runs once it is

    let cancelled = false;
    setRateLoading(true);
    setRateUnavailable(false);

    getDashboardData(
      commodity,
      (data) => {
        if (cancelled) return;
        const liveRate = parseFloat(data?.rate?.rate);
        if (liveRate > 0) {
          dispatch(setCommodityRates(commodity, liveRate));
          dispatch(setDashboardData(estId, commodity, data));
        } else {
          setRateUnavailable(true);
        }
        setRateLoading(false);
      },
      () => {
        if (cancelled) return;
        setRateUnavailable(true);
        setRateLoading(false);
      },
      estId,
    );

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [RATE, activeAccount?.estateId]);

  // Resumed after a Paystack redirect or a page refresh mid-payment — jump
  // straight to the result step instead of leaving the resident on step 1.
  useEffect(() => {
    if (!resumedPayment) return;
    setPaymentData({ billId: resumedPayment.billId, reference: resumedPayment.reference });
    setPaymentStatus(resumedPayment.status || "OUTCOME_UNKNOWN");
    setToken(resumedPayment.token || null);
    setShowSuccessPage(true);
    nextStep();
    nextStep();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumedPayment]);

  // Local, non-authoritative estimate — used only for the order-entry
  // page's live litre-field feedback while typing. The server quote below
  // (not this) is what the review/payment step and /order/initiate use.
  const unitPurchased =
    RATE > 0 && Number(amountPaid) > 0 ? Number(amountPaid) / RATE : 0;
  const amountCal = unit * RATE;
  const baseUtilityAmount =
    Number(amountPaid) > 0 ? Number(amountPaid) : Number(amountCal) > 0 ? Number(amountCal) : 0;

  // Server-authoritative checkout quote (Checkpoint 2C) — refetched
  // whenever the base utility amount, applied coupons, or estate change.
  // Debounced so typing an amount doesn't fire a request per keystroke.
  useEffect(() => {
    if (skipNextQuoteFetchRef.current) {
      skipNextQuoteFetchRef.current = false;
      return;
    }
    if (!(baseUtilityAmount > 0) || !estateId || !(RATE > 0)) {
      setQuote(null);
      setQuoteError(null);
      setQuoteLoading(false);
      return;
    }

    const seq = ++quoteRequestSeq.current;
    setQuoteLoading(true);
    setQuoteError(null);

    const handle = setTimeout(async () => {
      const { quote: result, error } = await getCheckoutQuote({
        utilityAmountNaira: baseUtilityAmount,
        estateId,
        commodityType: commodity.toUpperCase(),
        couponCodes: appliedCoupons.map((c) => c.code),
      });
      if (quoteRequestSeq.current !== seq) return; // superseded by a newer request
      setQuoteLoading(false);
      if (error) {
        setQuoteError(error);
        setQuote(null);
      } else {
        setQuote(result);
      }
    }, QUOTE_DEBOUNCE_MS);

    return () => { clearTimeout(handle); quoteRequestSeq.current++; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUtilityAmount, estateId, RATE, JSON.stringify(appliedCoupons.map((c) => c.code))]);

  const handleSubmit = () => {
    // Bank-transfer estates never reach Paystack from the resident side —
    // there is no "Buy" button wired to this in that mode (see reviewPage
    // below), but this guard is defense in depth against any other caller.
    const fullyCovered = quote?.fullyCoveredByCoupon === true && quote?.payableAmount === 0;
    if (initiationRef.current) return;
    if (!fullyCovered && (paymentMethod !== "PAYSTACK" || !settings.paystackAvailable)) return;
    // Defense in depth alongside the render-level block below — never allow
    // payment initiation to fire with an unresolved rate, e.g. a leftover
    // click queued before rateUnavailable rendered.
    if (!(RATE > 0)) {
      errorNotifier("Water rate is unavailable right now — please try again shortly.");
      return;
    }
    if (!quote || quoteLoading) {
      errorNotifier("Please wait for your checkout total to finish calculating.");
      return;
    }
    initiationRef.current = true;
    setPaymentLoading(true);

    const couponCodes = appliedCoupons.map((c) => c.code);
    const estateIdForOrder =
      activeAccount?.estateId ||
      (Array.isArray(userProfile?.estates) &&
        userProfile?.estates?.[0]?.estateId) ||
      null;

    // Send the BASE utility amount, never a client-computed gross/net total
    // — the backend independently recomputes the fee, coupon discount,
    // gross/payable amount, and unit count from this base value + its own
    // server-resolved rate. quoteFingerprint lets the backend detect if
    // anything material changed since the resident last reviewed this
    // total (Checkpoint 2C §6) — it is not itself trusted for pricing.
    const payload = {
      utilityAmountNaira: baseUtilityAmount,
      unitPurchased: quote.unitPurchased,
      meterId,
      paymentProvider: "PAYSTACK",
      estateId: estateIdForOrder,
      commodityType: (commodity || "").toUpperCase(),
      quoteFingerprint: quote.quoteFingerprint,
      ...(couponCodes.length > 0 && { couponCodes }),
    };

    initializePayment(payload)
      .then((data) => {
        if (!mountedRef.current) return;
        if (data?.fullyCoveredByCoupon) {
          // Fully covered by coupon(s) — the backend never initialized
          // Paystack; it already vended and finalized this bill
          // synchronously. Show the real result directly — never open the
          // Paystack checkout modal for a coupon-covered purchase.
          successNotifier("Order complete!");
          // Include the server-computed amounts (payableAmount, grossAmount,
          // unitPurchased) so the receipt reflects what was actually
          // charged/purchased, not the pre-quote base amount the resident
          // typed — see the SuccessPage/PrintReceiptButton fix below.
          setPaymentData({ ...data });
          setToken(data?.token || null);
          setPaymentStatus(data?.status || "CONFIRMED");
          setShowSuccessPage(true);
          clearStoredPendingPayment();
          nextStep();
          return;
        }

        setPaymentData({ ...data });
        // Persist billId+reference so a refresh or the Paystack redirect
        // itself can resume this payment even without in-memory state.
        setStoredPendingPayment({
          billId: data?.billId,
          reference: data?.makePayment?.data?.reference,
        });
        // open payment modal which will allow opening the checkout in a new tab
        setPaymentModal(true);
        nextStep();
      })
      .catch((e) => {
        if (!mountedRef.current) return;
        if (e?.response?.status === 409 && e?.response?.data?.code === "QUOTE_CHANGED") {
          const refreshedQuote = e.response.data.quote;
          setQuote(refreshedQuote);
          skipNextQuoteFetchRef.current = true;
          // Re-sync the applied-coupon chips to exactly what the server says
          // is actually applied on the refreshed quote — a coupon that
          // caused the staleness (expired/deactivated/max-used) is dropped
          // server-side, and without this the next Buy click would keep
          // resending the same now-invalid code and loop on QUOTE_CHANGED
          // forever instead of ever reaching the refreshed, purchasable total.
          setAppliedCoupons(
            (refreshedQuote?.appliedCoupons || []).map((c) => ({
              code: c.code,
              valueInNaira: c.valueInNaira,
            })),
          );
          errorNotifier("Your checkout total changed. Please review the updated amount.");
          return;
        }
        errorNotifier(parsePaymentError(e));
      })
      .finally(() => {
        initiationRef.current = false;
        if (mountedRef.current) setPaymentLoading(false);
      });
  };

  const verifyPayments = () => {
    const payload = {
      reference: paymentData?.makePayment?.data?.reference,
      billId: paymentData?.billId,
    };

    if (paymentData?.makePayment?.data?.authorization_url) {
      verifyPayment(payload)
        .then((resp) => {
          const status = resp?.data?.status || (resp?.data?.success ? "CONFIRMED" : "FAILED");
          if (status === "CONFIRMED") {
            successNotifier(resp?.data?.response || "Payment status updated");
          }
          setToken(status === "CONFIRMED" ? resp?.data?.token || null : null);
          setPaymentStatus(status);
          setShowSuccessPage(true);
          if (status === "CONFIRMED" || status === "FAILED" || status === "REFUNDED") {
            clearStoredPendingPayment();
          }
          nextStep();
        })
        .catch((e) => {
          errorNotifier(
            e?.response?.data?.data || "Payment verification failed",
          );
        })
        .finally((e) => {
          setPaymentModal(false);
        });
    }
  };

  const orderPage = (
    <Flex py={4}>
      <StepOrderForm
        nextStep={nextStep}
        userProfile={userProfile}
        amountPaid={amountPaid}
        setAmountPaid={setAmountPaid}
        meterId={meterId}
        // setMeterId={setMeterId}
        unitPurchased={unitPurchased}
        unit={unit}
        amountCal={amountCal}
        setUnit={setUnit}
        commodityType={commodity}
        appliedCoupons={appliedCoupons}
        setAppliedCoupons={setAppliedCoupons}
        quote={quote}
        quoteLoading={quoteLoading}
        quoteError={quoteError}
      />
    </Flex>
  );
  const reviewPage = (
    <Flex py={4}>
      <ReviewsAndPayments
        nextStep={nextStep}
        amountPaid={amountPaid}
        meterId={meterId}
        unitPurchased={unitPurchased}
        unit={unit}
        amountCal={amountCal}
        commodityType={utilityType}
        userProfile={userProfile}
        setShowSuccessPage={setShowSuccessPage}
        handleSubmit={handleSubmit}
        paymentData={paymentData}
        setPaymentData={setPaymentData}
        paymentLoading={paymentLoading}
        setPaymentLoading={setPaymentLoading}
        setPaymentModal={setPaymentModal}
        quote={quote}
        quoteLoading={quoteLoading}
        quoteError={quoteError}
        appliedCoupons={appliedCoupons}
        isBankTransfer={isBankTransfer}
        bankTransfer={settings.bankTransfer}
        paymentSettings={settings}
        commodity={commodity}
        unitLabel={unitLabel}
        paymentMethod={paymentMethod}
        setChosenPaymentMethod={setChosenPaymentMethod}
      />
    </Flex>
  );

  const completedPage = (
    <Flex py={4}>
      <PaymentModal
        verifyPayments={verifyPayments}
        paymentModal={paymentModal}
        setPaymentData={setPaymentData}
        paymentData={paymentData}
        setPaymentModal={setPaymentModal}
        showSuccessPage={showSuccessPage}
        Total={quote?.payableAmount}
        amountCalTotal={quote?.payableAmount}
      />
    </Flex>
  );

  const steps = [
    { label: "Order Water", content: orderPage },
    { label: "Review & Payment", content: reviewPage },
    { label: "Complete Order", content: completedPage },
  ];

  // Block the order/review flow entirely until the account is eligible to
  // buy water — never let a resident land on a blank/disabled Review &
  // Payment screen with no explanation. Checked in order: estate membership,
  // then meter assignment, then (below) the live rate. Does not apply once a
  // resumed payment or result page is being shown, since those don't depend
  // on any of this.
  if (!resumedPayment && !showSuccessPage) {
    if (profileLoading) {
      return <FullPageLoader />;
    }
    if (!estateId) {
      return (
        <Flex align="center" justify="center" w="100%" mt="60px">
          <VStack spacing={3} maxW="360px" textAlign="center">
            <Text fontSize="18px" fontWeight="bold" color="#050509">
              You need an estate to buy water
            </Text>
            <Text fontSize="14px" color="gray.600">
              You need to join or select an estate before you can buy water.
            </Text>
            <Button
              colorScheme="primary"
              bg="primary.500"
              color="white"
              onClick={() => navigate("/dashboard")}
            >
              Select an estate
            </Button>
          </VStack>
        </Flex>
      );
    }
    if (!meterId) {
      return (
        <Flex align="center" justify="center" w="100%" mt="60px">
          <VStack spacing={3} maxW="360px" textAlign="center">
            <Text fontSize="18px" fontWeight="bold" color="#050509">
              No water meter assigned
            </Text>
            <Text fontSize="14px" color="gray.600">
              No water meter is assigned to this estate account yet. Please
              contact your estate administrator.
            </Text>
          </VStack>
        </Flex>
      );
    }
    if (rateLoading) {
      return <FullPageLoader />;
    }
    if (rateBlocked) {
      return (
        <Flex align="center" justify="center" w="100%" mt="60px">
          <VStack spacing={3} maxW="360px" textAlign="center">
            <Text fontSize="18px" fontWeight="bold" color="#050509">
              Water rate unavailable
            </Text>
            <Text fontSize="14px" color="gray.600">
              {serverRate.authoritative
                ? "Rate unavailable. " +
                  (blockingReasonMessages(serverRate.reasons)[0] ||
                    "The water rate has not been set for this estate yet.") +
                  " Please contact your estate administrator."
                : "We couldn't load the current water rate for your estate. Please try again shortly."}
            </Text>
            {!serverRate.authoritative && <Button
              colorScheme="primary"
              bg="primary.500"
              color="white"
              onClick={() => window.location.reload()}
            >
              Retry
            </Button>}
          </VStack>
        </Flex>
      );
    }
  }

  return (
    <Flex
      flexDirection="column"
      width="100%"
      px={["0px", "0px", "50px"]}
      py={["20px", "20px", "50px"]}
      bg="#fff"
      borderRadius="20px"
    >
      <Flex mb="20px">
        <FiChevronLeft
          size={30}
          cursor="pointer"
          onClick={() => {
            prevStep();
            if (activeStep === 0) {
              if (typeof setDisplayPage === "function") {
                setDisplayPage("main");
              } else {
                navigate(-1);
              }
            }
          }}
        />
      </Flex>
      <Steps
        activeStep={activeStep}
        labelOrientation="vertical"
        colorScheme="primary"
        responsive={false}
        mb={["30px", "30px", "0"]}
      >
        {steps?.map(({ label, content }) => (
          <Step label={label} key={label}>
            {content}
          </Step>
        ))}
      </Steps>
      {showSuccessPage && (
        <SuccessPage
          userProfile={userProfile}
          // Prefer the server-computed amount actually charged
          // (paymentData.payableAmount, from /order/initiate's response) —
          // the pre-quote amountPaid/amountCal state is only a fallback for
          // the rare resumed-payment case where paymentData wasn't
          // repopulated with server amounts. Previously this always used
          // the pre-quote value, so a coupon or service fee made the
          // printed receipt disagree with what Paystack actually charged.
          amountPaid={paymentData?.payableAmount ?? amountPaid}
          unitPurchased={paymentData?.unitPurchased ?? unitPurchased}
          unit={unit}
          unitLabel={commodityUnit(commodity, paymentData?.unit || quote?.unit || unitLabel)}
          amountCal={amountCal}
          utilityType={utilityType}
          token={token}
          status={paymentStatus}
          onRetry={onRetry}
        />
      )}
      {/* {activeStep === steps.length ? (
        <Flex p={4}>
          <Button mx="auto" size="sm" onClick={reset}>
            Reset
          </Button>
        </Flex>
      ) : (
        <Flex width="100%" justify="flex-end">
          <Button
            isDisabled={activeStep === 0}
            mr={4}
            onClick={prevStep}
            size="sm"
            variant="ghost"
          >
            Prev
          </Button>
          <Button size="sm" onClick={nextStep}>
            {activeStep === steps.length - 1 ? "Finish" : "Next"}
          </Button>
        </Flex>
      )} */}
    </Flex>
  );
};
